import type { ColumnsRowsSettings } from '@/components/app/layout/columns-rows-guides-dialog'
import { useIsMobile } from '@/hooks/use-mobile'
import { DesktopMenu } from './components/desktop-menu'
import { MenubarDialogs } from './components/menubar-dialogs'
import { MobileMenu } from './components/mobile-menu'
import { buildEditMenuCommands } from './helpers/edit-menu-commands'
import { buildObjectMenuCommands } from './helpers/object-menu-commands'
import { useMenubarDialogs } from './hooks/use-menubar-dialogs'
import { useMenubarInputs } from './hooks/use-menubar-inputs'
import { useMenubarOpen } from './hooks/use-menubar-open'
import { useViewMenuToggles } from './hooks/use-view-menu-toggles'
import type { MenubarMenuModel } from './types/menubar-menu-model'

/** The hamburger menu (File, Edit, Object, View, Help) and its dialogs. Reads the editor contexts. */
export function MenubarButton() {
  const inputs = useMenubarInputs()
  const {
    gridMode = 'off',
    onMenuToggle,
    onClose,
    onToggleFullPica,
    onToggleHalfPica,
    onSetColumnsRows,
    rulerUnit = 'pica',
    columns = 1,
    rows = 1,
    colGutter = 24,
    rowGutter = 24,
    defaultGuttersLinked = true,
    pageInnerWidth = 561,
    pageInnerHeight = 736,
    isGuidesDialogOpen: isGuidesDialogOpenProp,
    onGuidesDialogOpenChange,
    preferencesValues: preferencesValuesProp,
    onPreferencesChange,
  } = inputs

  // Hook order keeps the original effect order: useIsMobile, view-prop sync,
  // 'select-tool-activated' listener, close-on-guides-open.
  const isMobile = useIsMobile()
  const viewToggles = useViewMenuToggles({ ...inputs, gridMode })
  const { open, handleMenuOpenChange, closeMenu } = useMenubarOpen(onMenuToggle)
  const dialogs = useMenubarDialogs({
    isGuidesDialogOpen: isGuidesDialogOpenProp,
    onGuidesDialogOpenChange,
    preferencesValues: preferencesValuesProp,
    onPreferencesChange,
    closeMenu,
  })

  const currentGuideSettings: ColumnsRowsSettings = {
    columns,
    rows,
    colGutter,
    rowGutter,
  }

  const model: MenubarMenuModel = {
    closeMenu,
    onClose,
    openPreferencesDialog: dialogs.openPreferencesDialog,
    openGuidesDialog: dialogs.openGuidesDialog,
    editCommands: buildEditMenuCommands(inputs),
    objectCommands: buildObjectMenuCommands(inputs),
    gridMode,
    onToggleFullPica,
    onToggleHalfPica,
    viewToggles,
  }

  const menuDialogs = (
    <MenubarDialogs
      isGuidesDialogOpen={dialogs.isGuidesDialogOpen}
      onGuidesDialogOpenChange={dialogs.setIsGuidesDialogOpen}
      rulerUnit={rulerUnit}
      guideSettings={currentGuideSettings}
      defaultGuttersLinked={defaultGuttersLinked}
      pageInnerWidth={pageInnerWidth}
      pageInnerHeight={pageInnerHeight}
      onSetColumnsRows={onSetColumnsRows}
      isPreferencesDialogOpen={dialogs.isPreferencesDialogOpen}
      onPreferencesDialogOpenChange={dialogs.setIsPreferencesDialogOpen}
      preferencesValues={dialogs.preferencesValues}
      onPreferencesSave={dialogs.setPreferencesValues}
    />
  )

  if (isMobile) {
    return (
      <>
        <MobileMenu open={open} onOpenChange={handleMenuOpenChange} model={model} />
        {menuDialogs}
      </>
    )
  }

  return (
    <div className="flex items-center gap-1">
      <DesktopMenu open={open} onOpenChange={handleMenuOpenChange} model={model} />
      {menuDialogs}
    </div>
  )
}
